import { TourSlide } from "../types";

/**
 * The client (passenger) deck for the portal release.
 *
 * Its own id, separate from the assistant deck's: an account is only ever offered one of the two, but
 * a shared id would mean dismissing one silently dismisses the other if that ever stops being true.
 */
export const CLIENT_PORTAL_REFRESH_CAMPAIGN_ID = "client-portal-refresh-2026-09";

/**
 * A function rather than a constant because the first slide names the operator, and that is only known
 * at render. Interpolating here keeps `AnnouncementTour` free of any templating: slides stay plain data
 * by the time they reach it.
 *
 * `operatorName` comes from `activeOperator`, which `useSetupClientAccount` resolves through
 * verifyProvider before `ClientAccountProvider` lets anything mount — so it is populated by the time
 * this renders. It can still be absent (a failed verify, a persisted record from before the field), so
 * the operator is dropped from the sentence rather than leaving a gap or a placeholder in the title.
 */
export const getClientPortalRefreshSlides = (operatorName?: string): TourSlide[] => [
  {
    eyebrow: "Welcome back",
    title: operatorName ? `Your ${operatorName} portal has a new look` : "Your portal has a new look",
    blocks: [
      {
        type: "paragraph",
        text: "We've refreshed the portal to make booking faster and easier. It's the same account you've always had — nothing has been lost or reset.",
      },
      {
        // No descriptions: these render as bare ticked lines, per the design.
        type: "checklist",
        items: [
          { title: "All your bookings and history" },
          { title: "Favorite locations, guest passengers and drivers" },
          { title: "Saved payment details and ride preferences" },
        ],
      },
    ],
  },
  {
    eyebrow: "What's moved",
    title: "Three things worth knowing",
    blocks: [
      {
        type: "iconlist",
        items: [
          {
            icon: "Add",
            title: "Book a Trip",
            description: "Now a button at the top right — or use the quick booking panel on your home page.",
          },
          {
            icon: "Star1",
            title: "My Favourites",
            description: "Locations, guest passengers and drivers now have their own tab in the top menu.",
          },
          {
            icon: "Setting2",
            title: "Settings",
            description: "Your profile, payment settings, notifications and assistants all live here.",
          },
        ],
      },
    ],
  },
  {
    eyebrow: "One last thing",
    title: "See it all in our walkthrough video",
    blocks: [
      {
        type: "video",
        title: "Take a quick tour",
        subtitle: "See where everything lives in the new portal",
        href: "https://rideminder.vids.io/videos/8c9adcbb1b16e9c406/client-web-app-refresh",
      },
    ],
    footnote: "Or skip it — everything works the same way underneath, and you can't break anything by exploring.",
  },
];
